import _ from "lodash";
import { type UnknownAction } from "@reduxjs/toolkit";
import {
  CREATE_MICRO_LEARNING_REQUEST,
  CREATING_MICRO_LEARNING_FAILED,
  CREATING_MICRO_LEARNING_SUCCEEDED,
  DELETE_MICROLEARNING_REQUEST,
  DELETING_MICROLEARNING_FAILED,
  DELETING_MICROLEARNING_SUCCEEDED,
  FETCH_MICROLEARNING_REQUEST,
  FETCH_MICROLEARNINGS_REQUEST,
  FETCHING_MICROLEARNING_FAILED,
  FETCHING_MICROLEARNING_SUCCEEDED,
  FETCHING_MICROLEARNINGS_FAILED,
  FETCHING_MICROLEARNINGS_SUCCEEDED,
  GENERATE_ARTICLE_REQUEST,
  GENERATING_ARTICLE_FAILED,
  GENERATING_ARTICLE_SUCCEEDED,
  WS_COACHING_ARTICLE_GENERATION_COMPLETED,
  INVALIDATE_MICROLEARNING_CREATION,
  INVALIDATE_MICROLEARNING_DELETION,
  GENERATE_PODCAST_FOR_MICRO_LEARNING_REQUEST,
  GENERATING_PODCAST_FOR_MICRO_LEARNING_SUCCEEDED,
  GENERATING_PODCAST_FOR_MICRO_LEARNING_FAILED,
  WS_COACHING_IDENTIFY_MODALITY_COMPLETED,
} from "src/redux/actions/actionTypes";
import {
  CreateMicroLearningErrorResponse,
  CreateMicroLearningResponse,
  DeleteMicroLearningErrorResponse,
  DeleteMicroLearningResponse,
  FetchMicroLearningErrorResponse,
  FetchMicroLearningResponse,
  FetchMicroLearningsErrorResponse,
  FetchMicroLearningsResponse,
  GenerateArticleRequest,
  MicroLearning,
} from "src/types/coaching/microLearning";

export type LearningModalityType = "article";
export type ArticleStatus = "pending" | "processing" | "completed" | "failed";

export interface ArticleLearning {
  status: ArticleStatus;
  startedDate: string;
  processingTime: number;
}

export interface LearningModality {
  type: LearningModalityType;
  article?: ArticleLearning;
}

interface MicroLearningState {
  loading: boolean;
  loaded: boolean;
  error: any;
  item: MicroLearning | null;
  generateArticle?: {
    generating: boolean;
    generated: boolean;
    error: any;
  } | null;
  generatePodcast?: {
    generating: boolean;
    generated: boolean;
    error: any;
  } | null;
}
interface MicroLearningsState {
  loading: boolean;
  loaded: boolean;
  error: any;
  item: {
    [key: string]: MicroLearningState;
  }
  items: MicroLearning[];
  pagination: {
    currentPage: number;
    totalPages: number;
    recordsPerPage: number;
    totalRecordsCount: number;
    hasMoreItems: boolean;
  };
  creation: MicroLearningCreationState | null;
  deletion: {
    [key: string]: MicroLearningDeletionState;
  };
}
interface MicroLearningCreationState {
  creating: boolean;
  created: boolean;
  item: MicroLearning | null;
  error: any;
}

interface MicroLearningDeletionState {
  id: string;
  deleting: boolean;
  deleted: boolean;
  error: any;
}

const initialMicroLearningDeletionState: MicroLearningDeletionState = {
  id: "",
  deleting: false,
  deleted: false,
  error: null,
};

const initialState: MicroLearningsState = {
  loading: false,
  loaded: false,
  error: null,
  item: {},
  items: [],
  pagination: {
    currentPage: 0,
    totalPages: 0,
    recordsPerPage: 0,
    totalRecordsCount: 0,
    hasMoreItems: false,
  },
  creation: null,
  deletion: {},
};

const initialMicroLearningCreationState: MicroLearningCreationState = {
  creating: false,
  created: false,
  item: null,
  error: null,
};

export function microLearningReducer(
  state: MicroLearningsState = initialState,
  action: UnknownAction,
): MicroLearningsState {
  switch (action.type) {
    case CREATE_MICRO_LEARNING_REQUEST: {
      return {
        ...state,
        creation: {
          ...initialMicroLearningCreationState,
          creating: true,
        },
      };
    }

    case CREATING_MICRO_LEARNING_SUCCEEDED: {
      const { response } = action.payload as CreateMicroLearningResponse;
      const creationState = state.creation;
      if (!creationState) {
        return state;
      }

      let records: MicroLearning[] = state.items || [];

      records = [response, ...records];
      records = _.uniqBy(records, "id");

      return {
        ...state,
        items: records,
        item: {
          ...state.item,
          [response.id]: {
            loading: false,
            loaded: true,
            item: response,
            error: null,
          },
        },
        creation: {
          ...creationState,
          creating: false,
          created: true,
          item: response,
          error: null,
        },
      };
    }

    case CREATING_MICRO_LEARNING_FAILED: {
      const { error } = action.payload as CreateMicroLearningErrorResponse;
      
      const creationState = state.creation;
      if (!creationState) {
        return state;
      }

      return {
        ...state,
        creation: {
          ...creationState,
          creating: false,
          created: true,
          error: error,
        },
      };
    }

    case FETCH_MICROLEARNINGS_REQUEST: {
      return {
        ...state,
        loading: true,
        loaded: false,
        error: null,
      };
    }

    case FETCHING_MICROLEARNINGS_SUCCEEDED: {
      const { response } = action.payload as FetchMicroLearningsResponse;
      
      const existingItems: MicroLearning[] = state.items || [];
      let records: MicroLearning[] = response.learnings || [];

      const pagination = response.pagination;

      if (pagination.pageNumber > 0) {
        records = [...existingItems, ...records];
      }

      return {
        ...state,
        loading: false,
        loaded: true,
        items: records,
        pagination: {
          currentPage: pagination.pageNumber,
          totalPages: pagination.totalPagesCount,
          recordsPerPage: pagination.itemsPerPage,
          totalRecordsCount: pagination.totalItemCount,
          hasMoreItems: pagination.pageNumber < pagination.totalPagesCount - 1,
        },
        error: null,
      };
    }

    case FETCHING_MICROLEARNINGS_FAILED: {
      const { error } = action.payload as FetchMicroLearningsErrorResponse;
      return {
        ...state,
        loading: false,
        loaded: false,
        items: [],
        error,
      };
    }

    case FETCH_MICROLEARNING_REQUEST: {
      const id = action.payload as string;

      const microLearningState = state.item[id] || {
        loading: false,
        loaded: false,
        item: null,
        error: null,
      };

      return {
        ...state,
        item: {
          ...state.item,
          [id]: {
            ...microLearningState,
            loading: true,
            loaded: false,
            error: null,
          },
        },
      };
    }

    case FETCHING_MICROLEARNING_SUCCEEDED: {
      const {id, response } = action.payload as FetchMicroLearningResponse;

      if(!response || !id) {
        return state;
      }

      const microLearningState = state.item[id];
      if(!microLearningState) {
        return state;
      }

      return {
        ...state,
        item: {
          ...state.item,
          [id]: {
            ...microLearningState,
            loading: false,
            loaded: true,
            item: {
              ...response,
            },
            generateArticle: {
              generating: false,
              generated: false,
              error: null,
            },
            generatePodcast: {
              generating: false,
              generated: false,
              error: null,
            },
            error: null,
          },
        },
      };
    }

    case FETCHING_MICROLEARNING_FAILED: {
      const {id, error } = action.payload as FetchMicroLearningErrorResponse;
      const microLearningState = state.item[id];
      if(!microLearningState) {
        return state;
      }
      
      return {
        ...state,
        item: {
          ...state.item,
          [id]: {
            ...microLearningState,
            loading: false,
            loaded: false,
            item: null,
            error: error,
          },
        },
      };
    }

    case GENERATE_ARTICLE_REQUEST: {
      const { id } = action.payload as GenerateArticleRequest;

      const microLearningState = state.item[id] || {
        loading: false,
        loaded: false,
        item: null,
        error: null,
      }; 

      return {
        ...state,
        item: {
          ...state.item,
          [id]: {
            ...microLearningState,
            generateArticle: {
              generating: true,
              generated: false,
              error: null,
            },
          },
        },
      };
    }

    case GENERATING_ARTICLE_SUCCEEDED: {
      const { id, response } = action.payload as any;

      if (!id) {
        return state;
      }

      const microLearningState = state.item[id] || {
        loading: false,
        loaded: false,
        item: null,
        error: null,
      };  

      if(!microLearningState) {
        return state;
      }

      return {
        ...state,
        item: {
          ...state.item,
          [id]: {
            ...microLearningState,
            item: {
              ...response
            },
            generateArticle: {
              generating: true,
              generated: true,
              error: null,
            },
          },
        },
      };
    }

    case GENERATING_ARTICLE_FAILED: {
      const { id, error } = action.payload as any;

      if (!id) {
        return state;
      }

      const editState = state.item;
      if (!editState || !editState[id]) {
        return state;
      }

      return {
        ...state,
        item: {
          ...editState,
          [id]: {
            ...editState[id],
            generateArticle: {
              generating: false,
              generated: false,
              error: error,
            },
          },
        },
      };
    }

    case DELETE_MICROLEARNING_REQUEST: {
      const id = action.payload as string;
      let deletionState =
        state.deletion[id] || initialMicroLearningDeletionState;

      deletionState = {
        ...deletionState,
        id,
        deleting: true,
        deleted: false,
        error: null,
      };

      return {
        ...state,
        deletion: {
          ...state.deletion,
          [id]: deletionState,
        },
      };
    }

    case DELETING_MICROLEARNING_SUCCEEDED: {
      const { id } = action.payload as DeleteMicroLearningResponse;
      const deletionState = state.deletion[id];
      if (!deletionState) {
        return state;
      }

      const items = state.items?.filter((item) => {
        return item.id !== id;
      });

      return {
        ...state,
        items: items,
        deletion: {
          ...state.deletion,
          [id]: {
            ...deletionState,
            deleting: false,
            deleted: true,
            error: null,
          },
        },
      };
    }

    case DELETING_MICROLEARNING_FAILED: {
      const { id, error } = action.payload as DeleteMicroLearningErrorResponse;

      const deletionState = state.deletion[id];
      if (!deletionState) {
        return state;
      }

      return {
        ...state,
        deletion: {
          ...state.deletion,
          [id]: {
            ...deletionState,
            deleting: false,
            deleted: false,
            error: error,
          },
        },
      };
    }


    case WS_COACHING_ARTICLE_GENERATION_COMPLETED: {
      const { id } = action.payload as any;

      const editState = state.item;
      if (!editState || !editState[id]) {
        return state;
      }

      return {
        ...state,
        item: {
          ...editState,
          [id]: {
            ...editState[id],
              generateArticle: {
                generating: false,
                generated: true,
                error: null,
              }
          },
        },
      };
    }

    case GENERATE_PODCAST_FOR_MICRO_LEARNING_REQUEST: {
      const id = action.payload as string;

      const microLearningState = state.item[id] || {
        loading: false,
        loaded: false,
        item: null,
        error: null,
      };

      return {
        ...state,
        item: {
          ...state.item,
          [id]: {
            ...microLearningState,
            generatePodcast: {
              generating: true,
              generated: false,
              error: null,
            },
          },
        },
      };
    }

    case GENERATING_PODCAST_FOR_MICRO_LEARNING_SUCCEEDED: {
      const { id } = action.payload as { id: string };

      if (!id) {
        return state;
      }

      const microLearningState = state.item[id];
      if (!microLearningState) {
        return state;
      }

      return {
        ...state,
        item: {
          ...state.item,
          [id]: {
            ...microLearningState,
            generatePodcast: {
              generating: false,
              generated: true,
              error: null,
            },
          },
        },
      };
    }

    case GENERATING_PODCAST_FOR_MICRO_LEARNING_FAILED: {
      const { id, error } = action.payload as { id: string; error: any };

      if (!id) {
        return state;
      }

      const editState = state.item;
      if (!editState || !editState[id]) {
        return state;
      }

      return {
        ...state,
        item: {
          ...editState,
          [id]: {
            ...editState[id],
            generatePodcast: {
              generating: false,
              generated: false,
              error: error,
            },
          },
        },
      };
    }

   
    case INVALIDATE_MICROLEARNING_CREATION: {
      return {
        ...state,
        creation: null,
      };
    }
    case INVALIDATE_MICROLEARNING_DELETION: {
      return {
        ...state,
        deletion: {},
      };
    }

    case WS_COACHING_IDENTIFY_MODALITY_COMPLETED: {
      const response = action.payload as any;

      if(!response) {
        return state;
      }

      const { id } = response;

      const editState = state.item;
      if (!editState || !editState[id]) {
        return state;
      }

      return {
        ...state,
        item: {
          ...editState,
          [id]: {
            ...editState[id],
            item: {
              ...editState[id].item,
              ...response,
            },
          },
        },
      };
    }

    default:
      return state;
  }
}
